import {
  GetPublicationsByUserIdQuery,
  GetPublicationsQuery,
  ImageGeneration,
  VideoGeneration,
  ImageGenerationType,
  ImageGenerationStatus,
  VideoGenerationStatus,
} from '@/.gql/graphql';

function getFileId(fileUrl: string | null): string {
  if (!fileUrl) {
    return '';
  }
  const url = new URL(fileUrl);
  return url.searchParams.get('mediaId') ?? '';
}

interface File {
  src: string;
  fileName: string;
  fileId: string;
  isPublished: boolean;
  status: ImageGenerationStatus | VideoGenerationStatus;
  reuseCount?: number;
}

interface VideoSerializedFile extends File {
  videoGenerationId: string;
}

interface ImageSerializedFile extends File {
  imageGenerationId: string;
  type: ImageGenerationType;
}

export interface PublicationFile extends File {
  publicationId: string;
  publisherId?: string;
}

export type SerializedFile =
  | VideoSerializedFile
  | ImageSerializedFile
  | PublicationFile;

export const serializeFile = (
  data:
    | VideoGeneration
    | GetPublicationsQuery['getPublications']['data'][number]
    | GetPublicationsByUserIdQuery['getPublicationsByUserId']['data'][number],
): SerializedFile => {
  if ('video' in data) {
    return {
      src: data.video ?? '',
      fileName: 'video.mp4',
      fileId: getFileId(data.video),
      isPublished: data.isPublished,
      videoGenerationId: data.id,
      status: data.status,
    };
  }

  const publisherId = 'Author' in data ? data.Author?.id : undefined;

  return {
    publicationId: data.id,
    src: data.fileUrl,
    fileName: 'image.png',
    fileId: data.fileId,
    isPublished: true,
    publisherId,
    status: VideoGenerationStatus.Completed,
  };
};

export const serializeFiles = (
  data: Array<ImageGeneration | VideoGeneration>,
): SerializedFile[] =>
  data.flatMap((generation) => {
    if ('video' in generation) {
      return serializeFile(generation);
    }
    const images = generation.images ?? [];

    return images.map(
      (file): SerializedFile => ({
        imageGenerationId: generation.id,
        type: generation.type,
        src: file,
        fileName: 'image.png',
        fileId: getFileId(file),
        isPublished: generation.publishedFileIds.includes(getFileId(file)),
        reuseCount:
          generation.publishedFileIdsRepostCount[getFileId(file)] ?? 0,
        status: generation.status,
      }),
    );
  });
